import React from 'react';
import { Col, Row } from 'reactstrap';
import Select from 'react-select';
import DragHandle from '../../events/components/QuestionnaireBuilder/DragHandle';
import DeleteIcon from '../../events/components/QuestionnaireBuilder/icons/DeleteIcon';
import UpArrowIcon from '../../events/components/QuestionnaireBuilder/icons/UpArrowIcon';
import DownArrowIcon from '../../events/components/QuestionnaireBuilder/icons/DownArrowIcon';
import QuestionType, {
  QuestionTypeEnum,
} from '../../events/components/QuestionnaireBuilder/QuestionType';
import t from '../../../util/translation/translation';
import { isValidAnswerAllowed, optionsAvailable } from './utils';

const mapQuestionType = (type) => {
  switch (type) {
    case 'TEXT_INPUT':
      return QuestionTypeEnum.TEXT_FIELD;
    case 'IMAGE_UPLOAD':
      return QuestionTypeEnum.IMAGE_UPLOAD;
    case 'CHECKBOX':
      return QuestionTypeEnum.CHECKBOX;
    case 'DROPDOWN':
      return QuestionTypeEnum.DROPDOWN;
    case 'RADIO_BUTTONS':
      return QuestionTypeEnum.RADIO_GROUP;
    case 'SCORE_PREDICTOR':
      return QuestionTypeEnum.SCORE_PREDICTOR;
    case 'PLAYER_PICKER':
      return QuestionTypeEnum.PLAYER_PICKER;
    default:
      return null;
  }
};

export default function QuestionItem(props) {
  const {
    question,
    onDelete,
    onMoveUp,
    handleInput,
    onMoveDown,
    type,
    availableTeams,
    showValidAnswerCheckbox,
    onValidAnswerChange,
    radioGroupName = 'valid-answer-group',
  } = props;
  const shouldMoveUpDisabled = props.first;
  const shouldMoveDownDisabled = props.last;
  const questionType = mapQuestionType(type);
  const getPlaceholder = () => {
    switch (type) {
      case 'RADIO_BUTTONS':
      case 'CHECKBOX':
      case 'DROPDOWN':
      case 'SCORE_PREDICTOR': {
        return t('questionaire.insertAnswerOption');
      }
      default:
        return '';
    }
  };
  const renderComponent = () => {
    if (type === 'PLAYER_PICKER') {
      return (
        <Select
          value={question.value}
          onChange={handleInput}
          options={availableTeams}
          className="form__form-group-select"
          placeholder={t('settingsObjectsShared.selectTeam')}
          multi={false}
        />
      );
    }
    return (
      <input
        placeholder={getPlaceholder()}
        disabled={type === 'TEXT_INPUT' || type === 'IMAGE_UPLOAD'}
        className="question-description"
        value={question.value}
        onInput={(e) => handleInput(e.target.value)}
      />
    );
  };
  const renderValidAnswerInput = () => {
    if (type === 'SCORE_PREDICTOR') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#666' }}>
            {t('questionaire.validAnswerNumber')}
          </span>
          <input
            type="number"
            className="form-control"
            style={{ width: '80px', height: '30px' }}
            value={question.isValidAnswer ?? ''}
            onChange={(e) =>
              onValidAnswerChange &&
              onValidAnswerChange(question.id, e.target.value)
            }
            placeholder="0"
            min="0"
          />
        </div>
      );
    }

    // If we are dealing with RADIO_BUTTONS question type, use radio inputs so that only one option can be marked as valid
    if (type === 'RADIO_BUTTONS') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '12px', color: '#666' }}>
            {t('questionaire.validAnswer')}
          </span>
          <input
            type="radio"
            id={`edit-answers-${question.id}`}
            name={radioGroupName}
            onClick={(e) => {
              e.stopPropagation();
              onValidAnswerChange &&
                onValidAnswerChange(question.id, e.target.checked);
            }}
            onChange={(e) => {
              /* `onChange` required for controlled input to avoid React warning */
              e.stopPropagation();
              onValidAnswerChange &&
                onValidAnswerChange(question.id, e.target.checked);
            }}
            checked={question.isValidAnswer || false}
          />
        </div>
      );
    }

    // Default (CHECKBOX, DROPDOWN etc.) behaviour -> checkbox per option
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontSize: '12px', color: '#666' }}>
          {t('questionaire.validAnswer')}
        </span>
        <input
          className="checkbox-btn__checkbox"
          type="checkbox"
          id={`edit-answers-${question.id}`}
          name={`edit-answers-${question.id}`}
          onChange={(e) =>
            onValidAnswerChange &&
            onValidAnswerChange(question.id, e.target.checked)
          }
          checked={question.isValidAnswer || false}
        />
      </div>
    );
  };
  if (type === 'TEXT_INPUT' || type === 'IMAGE_UPLOAD') {
    return null;
  }
  return (
    <div key={question.id} className="picked-question-card">
      <div className="content-area">
        <Row noGutters>
          <Col className="question-content">
            <div className="float-left draggable-handle">
              <DragHandle />
            </div>
            {renderComponent()}
          </Col>
          {optionsAvailable(type) && (
            <Col className="col-3">
              <div className="card-actions">
                <button
                  className="action-button red-border"
                  onClick={() => onDelete(question)}
                >
                  <DeleteIcon />
                </button>
                <button
                  className="action-button move-up"
                  disabled={shouldMoveUpDisabled}
                  onClick={() => onMoveUp(question)}
                >
                  <UpArrowIcon />
                </button>
                <button
                  className="action-button move-down"
                  disabled={shouldMoveDownDisabled}
                  onClick={() => onMoveDown(question)}
                >
                  <DownArrowIcon />
                </button>
              </div>
            </Col>
          )}
          <Col className="col-3 d-flex justify-content-end">
            <QuestionType type={questionType} />
          </Col>
          {isValidAnswerAllowed(type) && showValidAnswerCheckbox && (
            <Col className="col-3 d-flex justify-content-end align-items-center">
              {renderValidAnswerInput()}
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
}
